<template>
  <div class="scanner-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="scanner-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-icon-box">
          <v-icon color="#F2F2ED" size="20">mdi-qrcode-scan</v-icon>
        </div>
        <div>
          <h1 class="page-title">Escáner de asistencias</h1>
          <p class="page-sub">Registro de visitas en tiempo real</p>
        </div>
      </header>

      <!-- Aviso -->
      <section class="fade-in delay-100">
        <div class="info-box">
          <v-icon size="18" color="#8C8C93" class="mr-3 info-icon">mdi-rocket-launch-outline</v-icon>
          <div>
            <div class="info-title">Nueva función en desarrollo</div>
            <div class="info-text">Próximamente: historial de visitas y estadísticas en tiempo real</div>
          </div>
        </div>
      </section>

      <!-- Escáner -->
      <section class="fade-in delay-200 mt-lg">
        <div class="card-flat scanner-card">
          <div class="scanner-frame">
            <div class="scanner-guidelines">
              <div class="corner top-left"></div>
              <div class="corner top-right"></div>
              <div class="corner bottom-left"></div>
              <div class="corner bottom-right"></div>
              <div class="scan-line" :class="{ scanning: isScanning }"></div>
            </div>
            <video ref="video" width="100%" autoplay playsinline></video>
            <canvas ref="canvas" style="display:none;"></canvas>

            <div class="scanner-overlay" v-if="!isScanning">
              <v-icon size="48" color="#5C5C62" class="mb-md">mdi-camera-outline</v-icon>
              <div class="overlay-text">Cámara lista para escanear</div>
            </div>
          </div>

          <!-- Estadísticas -->
          <div class="scanner-stats" v-if="stats.total > 0">
            <div class="stat-block">
              <div class="stat-value">{{ stats.today }}</div>
              <div class="stat-label">Hoy</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <div class="stat-value">{{ stats.week }}</div>
              <div class="stat-label">Esta semana</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <div class="stat-value">{{ stats.total }}</div>
              <div class="stat-label">Total</div>
            </div>
          </div>

          <!-- Acciones -->
          <div class="scanner-actions">
            <button class="btn-primary" @click="startScanner" :disabled="isScanning || loading">
              <span v-if="loading" class="loading-inline"><span class="spinner-sm"></span>Iniciando</span>
              <span v-else-if="isScanning" class="loading-inline">
                <span class="pulse-dot"></span>
                Escaneando…
              </span>
              <span v-else>
                <v-icon size="16" class="mr-2">mdi-camera</v-icon>
                Iniciar escáner
              </span>
            </button>
            <button class="btn-danger-outline" @click="stopScanner" :disabled="!isScanning">
              <v-icon size="16" class="mr-2">mdi-stop-circle-outline</v-icon>
              Detener escaneo
            </button>
          </div>

          <!-- Historial -->
          <div class="scanner-history" v-if="recentScans.length > 0">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Escaneos recientes
            </span>
            <div class="history-row">
              <div v-for="(scan, index) in recentScans" :key="index" class="scan-card"
                :class="{ 'scan-card-error': !scan.success }">
                <v-icon size="18" :color="scan.success ? '#C8FF4D' : '#FF6B6B'">
                  {{ scan.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
                <div class="scan-id">{{ scan.success ? `ID ${scan.id}` : 'Error' }}</div>
                <div class="scan-time">{{ scan.time }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="showAlert" :color="alertType" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">
          {{ alertType === 'success' ? 'mdi-check-circle' : alertType === 'error' ? 'mdi-alert-circle' : 'mdi-information' }}
        </v-icon>
        <span>{{ alertMessage }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="showAlert = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import jsQR from 'jsqr';
import HttpService from '../../Servicios/HttpService';

export default {
    data: () => ({
        isScanning: false,
        // Indica si el usuario TIENE INTENCIÓN de estar escaneando (distinto
        // de isScanning, que refleja el estado real del stream). Se usa
        // para saber si, al recuperar el foco de la pestaña, debemos
        // reconectar la cámara sola o dejarla apagada porque el usuario la
        // detuvo a propósito.
        intentoActivo: false,
        showAlert: false,
        alertMessage: '',
        alertType: 'info',
        loading: false,
        stream: null,
        scanInterval: null,
        codigoPresente: false,
        framesSinDeteccion: 0,
        procesando: false,
        recentScans: [],
        stats: {
            today: 0,
            week: 0,
            total: 0
        }
    }),

    mounted() {
        this.loadStats();
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },

    methods: {
        async startScanner() {
            try {
                this.loading = true;

                // Por si venimos de una reconexión automática: limpiamos
                // cualquier intervalo/stream viejo antes de pedir uno nuevo,
                // para no terminar con dos temporizadores corriendo a la vez.
                if (this.scanInterval) {
                    clearInterval(this.scanInterval);
                    this.scanInterval = null;
                }
                if (this.stream) {
                    this.stream.getTracks().forEach(track => track.stop());
                }

                this.stream = await navigator.mediaDevices.getUserMedia({
                    video: { 
                        facingMode: "environment",
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                });
                this.$refs.video.srcObject = this.stream;
                await this.$nextTick();
                
                this.isScanning = true;
                this.intentoActivo = true;
                this.scanInterval = setInterval(this.processFrame, 150);

            } catch (error) {
                this.detenerPorErrorFatal('No se pudo acceder a la cámara: ' + error.message);
            } finally {
                this.loading = false;
            }
        },

        processFrame() {
            try {
                const video = this.$refs.video;
                const canvas = this.$refs.canvas;

                if (video.readyState !== 4 || !this.isScanning) return;

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height);

                if (code) {
                    // Cada vez que SÍ detectamos el QR, reiniciamos el
                    // contador de "frames sin detección" — sigue en cuadro.
                    this.framesSinDeteccion = 0;

                    // Solo disparamos el registro la PRIMERA vez que vemos el
                    // código; mientras siga en cuadro, no se vuelve a
                    // procesar. Se reactiva solo cuando el QR desaparece de
                    // verdad (varios frames seguidos sin detectarlo, ver
                    // bloque else) y vuelve a aparecer.
                    if (!this.codigoPresente && !this.procesando) {
                        this.codigoPresente = true;
                        this.handleQRCode(code.data);
                    }
                } else {
                    // No detectamos el QR en este frame — pero eso puede ser
                    // solo un frame borroso por movimiento (muy común con
                    // cámara de celular en mano), no que el código
                    // realmente se haya retirado. Exigimos varios frames
                    // seguidos sin detección (aprox. 750ms a 150ms/frame)
                    // antes de "soltar" el código y permitir un reescaneo.
                    this.framesSinDeteccion++;
                    if (this.framesSinDeteccion >= 5) {
                        this.codigoPresente = false;
                    }
                }
            } catch (error) {
                console.error("Error en processFrame:", error);
            }
        },

        async handleQRCode(data) {
            this.procesando = true;

            try {
                const qrData = JSON.parse(data);
                if (!qrData.miembroId) throw new Error('QR no válido');

                this.alertMessage = 'Verificando membresía...';
                this.alertType = 'info';
                this.showAlert = true;

                // Antes de registrar, consultamos al miembro para validar su
                // estado y obtener idMembresia — misma regla que ya aplica
                // RegistrarVisita.vue (el botón de registrar solo aparece si
                // estado === 'ACTIVO'). El scanner no debe saltarse esa regla.
                const miembro = await HttpService.obtenerConDatos(
                    { metodo: "obtener_id", id: qrData.miembroId },
                    "miembros.php"
                );

                if (!miembro) {
                    throw new Error('Miembro no encontrado');
                }

                if (miembro.estado !== 'ACTIVO') {
                    const motivo = miembro.estado === 'VENCIDO'
                        ? 'Membresía vencida'
                        : 'Sin membresía activa';
                    throw new Error(`${motivo} — diríjase a recepción`);
                }

                this.alertMessage = 'Registrando visita...';

                const payload = {
                    metodo: "registrar",
                    visita: {
                        idMiembro: miembro.id,
                        idMembresia: miembro.idMembresia,
                        idUsuario: localStorage.getItem("idUsuario"),
                    }
                };

                const resultado = await HttpService.registrar(payload, "visitas.php");

                if (resultado && resultado.success) {
                    this.alertType = 'success';
                    this.alertMessage = `✅ Visita registrada: ${miembro.nombre}`;
                    
                    // Agregar a historial
                    this.recentScans.unshift({
                        id: miembro.nombre,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        success: true
                    });
                    
                    if (this.recentScans.length > 5) {
                        this.recentScans.pop();
                    }
                    
                    // Actualizar estadísticas
                    this.stats.today++;
                    this.stats.total++;
                    
                    this.playSound('success');
                    
                    setTimeout(() => {
                        this.showAlert = false;
                    }, 3000);
                } else if (resultado && resultado.duplicado) {
                    // No es un error del sistema, es una regla de negocio:
                    // este miembro ya entró hace poco. Se avisa distinto
                    // (amarillo, sonido más neutro) que un error real.
                    this.alertType = 'warning';
                    this.alertMessage = `⏱️ ${miembro.nombre}: ${resultado.message}`;
                    this.recentScans.unshift({
                        id: `${miembro.nombre} (ya registrado)`,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        success: false
                    });
                    this.playSound('warning');
                } else {
                    throw new Error((resultado && resultado.message) || 'No se pudo registrar la visita');
                }

            } catch (error) {
                this.showError(`❌ ${error.message}`);
                this.recentScans.unshift({
                    id: 'Error',
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    success: false
                });
                this.playSound('error');
            } finally {
                this.procesando = false;
            }
        },

        stopScanner() {
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
            }
            this.codigoPresente = false;
            this.framesSinDeteccion = 0;
            this.procesando = false;
            this.intentoActivo = false;
            if (this.scanInterval) {
                clearInterval(this.scanInterval);
            }
            this.isScanning = false;
            if (this.$refs.video) {
                this.$refs.video.srcObject = null;
            }
        },

        // Se dispara cuando la pestaña vuelve a estar visible (el usuario
        // regresa a esta ventana/tab después de un rato, o la laptop
        // despierta). Algunos navegadores/SO pausan o matan el MediaStream
        // de la cámara cuando la pestaña pierde el foco por mucho tiempo,
        // aunque nunca se haya llamado stopScanner(). Aquí detectamos ese
        // caso y reconectamos solo. Si el usuario había apagado el scanner
        // a propósito (intentoActivo = false), no hacemos nada.
        async handleVisibilityChange() {
            if (document.visibilityState !== 'visible' || !this.intentoActivo) {
                return;
            }

            const trackViva = this.stream &&
                this.stream.getVideoTracks().some(track => track.readyState === 'live');

            if (!trackViva) {
                console.warn('El stream de la cámara se perdió; reconectando…');
                await this.startScanner();
            }
        },

        showError(message) {
            // Errores "leves": un QR ilegible, miembro no encontrado,
            // membresía vencida, un timeout de red puntual. NO apagan la
            // cámara — el escáner debe poder quedarse abierto todo el día
            // sin que un mal escaneo lo detenga y obligue a reiniciarlo
            // manualmente.
            this.alertType = 'error';
            this.alertMessage = message;
            this.showAlert = true;
        },

        detenerPorErrorFatal(message) {
            // Errores fatales: no hay cámara, no hay permiso, el stream se
            // perdió. Aquí sí tiene sentido detener todo, porque no hay
            // nada que el escáner pueda seguir haciendo.
            this.alertType = 'error';
            this.alertMessage = message;
            this.showAlert = true;
            this.stopScanner();
        },

        playSound(type) {
            // Generamos el tono directamente con Web Audio API en vez de
            // cargar un mp3 de un CDN externo: suena al instante, funciona
            // sin conexión a internet, y no depende de que un servicio
            // externo (mixkit.co) esté disponible en ese momento.
            try {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                const ctx = new AudioCtx();

                const tono = (frecuencia, inicio, duracion) => {
                    const oscillator = ctx.createOscillator();
                    const gain = ctx.createGain();
                    oscillator.connect(gain);
                    gain.connect(ctx.destination);
                    oscillator.type = 'sine';
                    oscillator.frequency.value = frecuencia;
                    gain.gain.setValueAtTime(0.001, ctx.currentTime + inicio);
                    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + inicio + 0.01);
                    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + inicio + duracion);
                    oscillator.start(ctx.currentTime + inicio);
                    oscillator.stop(ctx.currentTime + inicio + duracion);
                };

                if (type === 'success') {
                    // Dos notas ascendentes cortas — "listo, adelante"
                    tono(880, 0, 0.1);
                    tono(1175, 0.1, 0.15);
                } else if (type === 'warning') {
                    // Un solo tono medio — "ya se registró, ya pasa"
                    tono(520, 0, 0.18);
                } else if (type === 'error') {
                    // Tono grave — algo no salió bien
                    tono(220, 0, 0.3);
                }
            } catch (e) {
                console.warn('No se pudo reproducir el sonido:', e);
            }
        },

        async loadStats() {
            try {
                const respuesta = await HttpService.obtenerConDatos(
                    { metodo: "stats" },
                    "visitas.php"
                );
                this.stats = {
                    today: parseInt(respuesta.hoy) || 0,
                    week: parseInt(respuesta.semana) || 0,
                    total: parseInt(respuesta.total) || 0
                };
            } catch (error) {
                console.error("Error cargando estadísticas:", error);
            }
        }
    },

    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.stopScanner();
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.scanner-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.scanner-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.scanner-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 640px;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== INFO BOX ===== */
.info-box {
  display: flex;
  align-items: flex-start;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 14px 16px;
}

.info-icon {
  margin-top: 1px;
}

.info-title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.info-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.scanner-card {
  padding: 24px;
}

/* ===== SCANNER FRAME ===== */
.scanner-frame {
  position: relative;
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--bg);
  border: 1px solid var(--card-border);
  min-height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanner-guidelines {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  pointer-events: none;
}

.corner {
  position: absolute;
  width: 28px;
  height: 28px;
  border: 2px solid var(--accent);
}

.corner.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
}

.corner.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
}

.corner.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
}

.corner.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
}

.scan-line {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  height: 2px;
  background: var(--accent);
  border-radius: 2px;
  opacity: 0;
  box-shadow: 0 0 10px rgba(200, 255, 77, 0.6);
}

.scan-line.scanning {
  opacity: 1;
  animation: scan 2s ease-in-out infinite;
}

@keyframes scan {
  0%, 100% { top: 16px; }
  50% { top: calc(100% - 18px); }
}

video {
  width: 100%;
  max-height: 420px;
  display: block;
}

.scanner-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(14, 14, 16, 0.9);
}

.overlay-text {
  color: var(--text-secondary);
  font-size: 13px;
}

/* ===== STATS ===== */
.scanner-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px;
  margin-top: 16px;
}

.stat-block {
  text-align: center;
  padding: 0 20px;
}

.stat-value {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}

.stat-label {
  font-size: 10.5px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 6px;
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: var(--card-border);
}

/* ===== ACTIONS ===== */
.scanner-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.btn-primary {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 13px 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-danger-outline {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--error);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: var(--radius);
  padding: 13px 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-danger-outline:hover:not(:disabled) {
  background: rgba(255, 107, 107, 0.08);
}

.btn-danger-outline:disabled {
  opacity: 0.4;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--bg);
  animation: pulse 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ===== HISTORY ===== */
.scanner-history {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 14px;
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.history-row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.scan-card {
  flex-shrink: 0;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 16px;
  min-width: 110px;
  text-align: center;
}

.scan-card-error {
  border-color: rgba(255, 107, 107, 0.25);
}

.scan-id {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 6px;
}

.scan-time {
  font-size: 10.5px;
  color: var(--text-tertiary);
  margin-top: 2px;
  font-family: var(--font-mono);
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .scan-line, .spinner-sm, .pulse-dot { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 600px) {
  .scanner-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .scanner-card {
    padding: 16px;
  }

  .scanner-actions {
    flex-direction: column;
  }

  .scanner-stats {
    flex-direction: column;
    gap: 14px;
  }

  .stat-divider {
    width: 60%;
    height: 1px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Miembros/ScannerQR.vue